// Phone cameras produce 4–12 MB photos, far more than a postcard needs and more
// than the upload limit will take.
// Shrink them in the browser before they ever leave the device.

const MAX_EDGE = 2048;
// Stay under the 6 MB MAX_UPLOAD_MB (and nginx's matching client-max-body-size),
// leaving room for the multipart envelope.
const TARGET_BYTES = 5 * 1024 * 1024;
const QUALITIES = [0.85, 0.75, 0.65, 0.5];

/**
 * Returns a JPEG no larger than MAX_EDGE on its longest side, lowering quality
 * until it fits TARGET_BYTES. Files that are already small enough, GIFs (which
 * may be animated) and anything the browser cannot decode pass through as-is,
 * leaving the server to accept or reject them.
 */
export async function shrinkImage(file: File): Promise<Blob> {
  if (file.type === 'image/gif') return file;

  let bitmap: ImageBitmap;
  try {
    // from-image applies EXIF orientation so portrait photos stay upright.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }

  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= TARGET_BYTES) return file;

    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    // JPEG has no alpha; paint transparent PNGs onto white rather than black.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);

    let out: Blob | null = null;
    for (const q of QUALITIES) {
      out = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
      if (out && out.size <= TARGET_BYTES) break;
    }
    return out && out.size < file.size ? out : file;
  } finally {
    bitmap.close();
  }
}
